import { z } from 'zod';
import { normalizeSupportedSchemaType } from '../utils/schemaTypes.js';
const schemaTypeSchema = z.string().trim().min(1).transform((type, ctx) => {
    const normalized = normalizeSupportedSchemaType(type);
    if (!normalized) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Unsupported schema type "${type}"`,
        });
        return z.NEVER;
    }
    return normalized;
});
// URL Validation Schema
export const urlSchema = z.string().url().min(1).max(2048).refine((url) => {
    try {
        const parsedUrl = new URL(url);
        return ['http:', 'https:'].includes(parsedUrl.protocol);
    }
    catch {
        return false;
    }
}, 'URL must use HTTP or HTTPS protocol').transform((url) => {
    // Strip the client-side fragment (#about, #faq, ...). The server returns
    // identical HTML regardless of the hash, and anchor-scroll scripts can
    // destroy the page's JS execution context mid-scrape ("Execution context
    // was destroyed"). Dropping it also collapses #a/#b/#c into one cache key.
    try {
        const parsedUrl = new URL(url);
        parsedUrl.hash = '';
        return parsedUrl.toString();
    }
    catch {
        return url;
    }
});
// Schema Generation Request Schema
export const schemaGenerationRequestSchema = z.object({
    url: urlSchema,
    schemaType: schemaTypeSchema.default('Auto'),
    options: z.object({
        includeImages: z.boolean().default(true),
        includeVideos: z.boolean().default(true),
        includeProducts: z.boolean().default(true),
        includeEvents: z.boolean().default(true),
        includeArticles: z.boolean().default(true),
        includeOrganization: z.boolean().default(true),
        includeLocalBusiness: z.boolean().default(true),
        requestedSchemaTypes: z.array(schemaTypeSchema).optional(),
        excludeSchemaTypes: z.array(schemaTypeSchema).optional(),
    }).optional()
});
// Schema Validation Request Schema
export const schemaValidationRequestSchema = z.object({
    schema: z.record(z.any()),
    strict: z.boolean().default(false)
});
// Credit Purchase Schema
export const creditPurchaseSchema = z.object({
    creditPackId: z.string().uuid(),
    successUrl: z.string().url().optional(),
    cancelUrl: z.string().url().optional()
});
// User Profile Update Schema
export const userProfileUpdateSchema = z.object({
    firstName: z.string().min(1).max(50).optional(),
    lastName: z.string().min(1).max(50).optional(),
    email: z.string().email().optional(),
    organizationName: z.string().min(1).max(100).optional()
});
// Credit Transaction Schema
export const creditTransactionSchema = z.object({
    type: z.enum(['purchase', 'usage', 'refund', 'bonus']),
    amount: z.number().int(),
    description: z.string().min(1).max(255),
    stripePaymentIntentId: z.string().optional()
});
// Usage Analytics Schema
export const usageAnalyticsSchema = z.object({
    action: z.enum(['schema_generation', 'schema_validation', 'credit_purchase', 'login', 'signup']),
    metadata: z.record(z.any()).optional(),
    ipAddress: z.string().ip().optional(),
    userAgent: z.string().max(500).optional()
});
// Pagination Schema
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(10)
});
// Credit Pack Schema
export const creditPackSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100),
    credits: z.number().int().min(1),
    priceInCents: z.number().int().min(1),
    savings: z.number().min(0).max(100).optional(),
    isPopular: z.boolean().default(false)
});
// JSON-LD Schema Validation
export const jsonLdSchemaSchema = z.object({
    '@context': z.union([z.string(), z.array(z.string())]),
    '@type': z.string(),
}).passthrough(); // Allow additional properties
// API Response Schemas
export const apiResponseSchema = z.object({
    success: z.boolean(),
    data: z.any().optional(),
    error: z.string().optional(),
    message: z.string().optional()
});
export const paginatedResponseSchema = z.object({
    data: z.array(z.any()),
    pagination: z.object({
        page: z.number().int(),
        limit: z.number().int(),
        total: z.number().int(),
        hasNext: z.boolean(),
        hasPrevious: z.boolean()
    })
});
